import axios from 'axios';
import { useQuery } from '@tanstack/react-query';

export type Row = Record<string, any>;
/** document families of the approval engine */
export type Kind = 'S' | 'F' | 'P' | 'A';

export const KIND_LABEL: Record<Kind, string> = {
  S: 'Purchasing, sales & stock',
  F: 'Finance',
  P: 'HR',
  A: 'Assets',
};

/** POST approvalSetup/<path>; throws the server's message when status is false */
export const call = async <T = any>(path: string, body: object = {}): Promise<{ result: T; message: string }> => {
  const data = await axios
    .post(`approvalSetup/${path}`, body)
    .then((r) => r.data)
    .catch((e) => {
      if (e?.response?.data?.message) return e.response.data;
      throw e;
    });
  if (!data.status) throw new Error(data.message || 'Something went wrong. Nothing was changed.');
  return { result: data.result as T, message: data.message };
};

export interface Rights {
  view: boolean;
  edit: boolean;
  create: boolean;
}
export const useRights = () =>
  useQuery({
    queryKey: ['approvalSetupRights'],
    queryFn: () => call<{ rules: Rights; delegation: Rights }>('rights').then((r) => r.result),
    staleTime: 5 * 60_000,
  });

export interface Flow {
  ID: number;
  CODE: string;
  NAME: string;
  STATUS: string;
  STEPS: number;
  PATH: string | null;
  BINDINGS: number;
  PENDING: number;
  EMPTY_STEPS: number;
}
export const useFlows = (path = 'flows') =>
  useQuery({ queryKey: ['approvalFlows', path], queryFn: () => call<Flow[]>(path).then((r) => r.result ?? []) });

export interface Group {
  CODE: string;
  NAME: string;
  MEMBERS: number;
}
export const useGroups = () =>
  useQuery({ queryKey: ['approvalGroups'], queryFn: () => call<Group[]>('groups').then((r) => r.result ?? []), staleTime: 5 * 60_000 });

export const useDivisions = () =>
  useQuery({
    queryKey: ['approvalDivisions'],
    queryFn: () => call<{ value: string; label: string }[]>('divisions').then((r) => (r.result ?? []).map((x: Row) => ({ code: String(x.VALUE ?? x.value), name: x.LABEL ?? x.label }))),
    staleTime: 5 * 60_000,
  });

export const stateTone = (s: string) =>
  (({ ACTIVE: 'ok', SCHEDULED: 'info', ENDED: 'muted', CANCELLED: 'bad' }) as Record<string, 'ok' | 'info' | 'muted' | 'bad'>)[s] ?? 'muted';

export const tone = (t: 'ok' | 'warn' | 'bad' | 'muted' | 'info') =>
  ({
    ok: { bgcolor: '#dcfce7', color: '#166534' },
    warn: { bgcolor: '#fef3c7', color: '#92400e' },
    bad: { bgcolor: '#fee2e2', color: '#991b1b' },
    info: { bgcolor: '#e0f2fe', color: '#075985' },
    muted: { bgcolor: '#f1f5f9', color: '#64748b' },
  })[t];

export const fmtDate = (s?: string | null) => {
  if (!s) return '';
  const d = new Date(`${s}T00:00:00`);
  return isNaN(+d) ? s : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};
